import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useTutorial } from "@/contexts/TutorialContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { X, ChevronDown } from "lucide-react";

export default function TutorialTooltip({ step, targetId }) {
  const { tutorialActive, tutorialStep, skipTutorial } = useTutorial();
  const { t } = useLanguage();
  const [coords, setCoords] = useState(null);
  const [smartPosition, setSmartPosition] = useState("bottom");
  const [showScrollArrow, setShowScrollArrow] = useState(false);
  const tooltipRef = useRef(null);

  const isActive = tutorialActive && tutorialStep === step;

  useEffect(() => {
    if (!isActive) return;

    const updatePosition = () => {
      const target = document.querySelector(`[data-testid="${targetId}"]`);
      
      if (step === 10 && !target) {
        const doneBtn = document.querySelector('[data-testid="recipe-mark-done-btn"]');
        if (doneBtn) {
          const rect = doneBtn.getBoundingClientRect();
          if (rect.top > window.innerHeight) {
            setShowScrollArrow(true);
            setCoords(null);
            return;
          }
        }
      }
      
      if (!target) {
        setCoords(null);
        return;
      }
      
      setShowScrollArrow(false);

      const rect = target.getBoundingClientRect();
      const computedStyle = window.getComputedStyle(target);
      const borderRadius = parseInt(computedStyle.borderRadius) || 16;

      const tooltipEl = tooltipRef.current;
      const tooltipHeight = tooltipEl?.getBoundingClientRect().height || 140;
      const tooltipWidth = 290;
      const minGap = 16;
      const navBarHeight = 85;

      let position = "bottom";
      const spaceBelow = window.innerHeight - rect.bottom - navBarHeight;
      
      if (spaceBelow < 160 || rect.bottom > window.innerHeight - navBarHeight - 140) {
        position = "top";
      }

      setSmartPosition(position);

      let top, left;
      if (position === "top") {
        top = rect.top - tooltipHeight - minGap;
      } else {
        top = rect.bottom + minGap;
      }
      left = rect.left + rect.width / 2 - tooltipWidth / 2;
      left = Math.max(12, Math.min(left, window.innerWidth - tooltipWidth - 12));

      setCoords({ 
        top, 
        left, 
        targetRect: rect,
        borderRadius 
      });
    };

    const timer1 = setTimeout(updatePosition, 50);
    const timer2 = setTimeout(updatePosition, 200);
    const timer3 = setTimeout(updatePosition, 500);

    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, [isActive, targetId, step]);

  if (!isActive) return null;
  
  if (showScrollArrow && step === 10) {
    return createPortal(
      <>
        <div className="fixed inset-0 bg-black/50 z-[9998] pointer-events-none" />
        <div className="fixed inset-0 z-[9999] flex items-center justify-center pointer-events-none">
          <div className="flex flex-col items-center animate-bounce">
            <p className="font-body text-white text-sm mb-2 px-4 py-2 bg-black/60 rounded-full">
              {t("scrollDown") || "Прогорни вниз"}
            </p>
            <ChevronDown className="w-10 h-10 text-white" strokeWidth={2} />
          </div>
        </div>
        <button
          onClick={skipTutorial}
          className="fixed top-6 right-6 z-[10000] w-10 h-10 flex items-center justify-center rounded-full bg-white/90 text-[#6B705C]"
        >
          <X className="w-5 h-5" strokeWidth={2} />
        </button>
      </>,
      document.body
    );
  }
  
  if (!coords) return null;

  const TUTORIAL_TEXTS = {
    1: t("tutorialStep1"),
    2: t("tutorialStep2"),
    3: t("tutorialStep3"),
    4: t("tutorialStep4"),
    5: t("tutorialStep5"),
    6: t("tutorialStep6"),
    7: t("tutorialStep7"),
    8: t("tutorialStep8"),
    9: t("tutorialStep9"),
    10: t("tutorialStep10"),
  };

  const { targetRect, borderRadius } = coords;
  const padding = 8; // Padding for green border
  const overlayPadding = 2; // Smaller padding for overlay cutout
  
  // Cutout area for overlay (smaller, closer to button)
  const cutX = targetRect.left - overlayPadding;
  const cutY = targetRect.top - overlayPadding;
  const cutW = targetRect.width + overlayPadding * 2;
  const cutH = targetRect.height + overlayPadding * 2;
  
  // Green border area (larger, with more padding)
  const borderX = targetRect.left - padding;
  const borderY = targetRect.top - padding;
  const borderW = targetRect.width + padding * 2;
  const borderH = targetRect.height + padding * 2;
  const borderR = Math.min(borderRadius + padding, borderW / 2, borderH / 2);

  // Screen dimensions
  const screenW = window.innerWidth;
  const screenH = window.innerHeight;

  return createPortal(
    <>
      {/* 4 overlay divs around cutout - blocks clicks only outside the hole */}
      {/* Top region */}
      <div 
        className="fixed z-[9990]"
        style={{ top: 0, left: 0, width: screenW, height: Math.max(0, cutY), backgroundColor: 'rgba(0,0,0,0.75)' }}
      />
      {/* Bottom region */}
      <div 
        className="fixed z-[9990]"
        style={{ top: cutY + cutH, left: 0, width: screenW, height: Math.max(0, screenH - cutY - cutH), backgroundColor: 'rgba(0,0,0,0.75)' }}
      />
      {/* Left region */}
      <div 
        className="fixed z-[9990]"
        style={{ top: cutY, left: 0, width: Math.max(0, cutX), height: cutH, backgroundColor: 'rgba(0,0,0,0.75)' }}
      />
      {/* Right region */}
      <div 
        className="fixed z-[9990]"
        style={{ top: cutY, left: cutX + cutW, width: Math.max(0, screenW - cutX - cutW), height: cutH, backgroundColor: 'rgba(0,0,0,0.75)' }}
      />

      {/* Oval green border around target - pointer-events none */}
      <div
        className="fixed z-[9991] pointer-events-none"
        style={{
          top: borderY,
          left: borderX,
          width: borderW,
          height: borderH,
          borderRadius: borderR,
          border: '3px solid #7B9E7F',
          boxShadow: '0 0 12px rgba(123, 158, 127, 0.5)',
        }}
      />

      {/* Tooltip - pointer-events none to allow clicks through */}
      <div
        ref={tooltipRef}
        data-testid={`tutorial-tooltip-${step}`}
        className="fixed z-[9999] w-[290px] bg-white shadow-2xl px-5 py-4 pointer-events-none"
        style={{ 
          top: coords.top, 
          left: coords.left,
          borderRadius: '20px',
          border: '1px solid rgba(0,0,0,0.08)',
        }}
      >
        <div className="flex items-center justify-between mb-3">
          <span 
            className="font-body font-bold text-[#7B9E7F] text-xs px-3 py-1"
            style={{ backgroundColor: 'rgba(123, 158, 127, 0.1)', borderRadius: '10px' }}
          >
            {step}/10
          </span>
          <button
            data-testid="tutorial-skip-btn"
            onClick={skipTutorial}
            className="w-7 h-7 flex items-center justify-center rounded-full bg-[#F2E9E4] text-[#A5A58D] hover:bg-[#E8E5E0] pointer-events-auto"
          >
            <X className="w-4 h-4" strokeWidth={2} />
          </button>
        </div>

        <p className="font-body text-[#3D4035] text-sm leading-relaxed">
          {TUTORIAL_TEXTS[step]}
        </p>

        <div
          className="absolute w-4 h-4 bg-white"
          style={{
            borderRadius: '2px',
            transform: 'rotate(45deg)',
            ...(smartPosition === "bottom" 
              ? { top: '-7px', left: '50%', marginLeft: '-8px', boxShadow: '-1px -1px 0 rgba(0,0,0,0.08)' }
              : { bottom: '-7px', left: '50%', marginLeft: '-8px', boxShadow: '1px 1px 0 rgba(0,0,0,0.08)' }
            ),
          }}
        />
      </div>
    </>,
    document.body
  );
}
